import LoadingSpinner from "@BrasselerDental/Components/LoadingSpinner/LoadingSpinner";
import CustomApplicationState from "@BrasselerDental/Store/CustomApplicationState";
import getSearchSuggestionsHandler from "@BrasselerDental/Store/Data/Search/Handlers/GetSuggestion";
import updateSearchResults from "@BrasselerDental/Store/Data/Search/Handlers/SetSearchResult";
import BrasselerColors from "@BrasselerDental/Utils/Colors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { addSearchHistory } from "@insite/client-framework/Services/AutocompleteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteBrandsStyles } from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import { AutocompleteCategoriesStyles } from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import { AutocompleteContentStyles } from "@insite/content-library/Widgets/Header/AutocompleteContent";
import { AutocompleteProductsStyles } from "@insite/content-library/Widgets/Header/AutocompleteProducts";
import { AutocompleteSuggestionsStyles } from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import { SearchHistoryStyles } from "@insite/content-library/Widgets/Header/SearchHistory";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Search from "@insite/mobius/Icons/Search";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement | null>) => PositionStyle; //upgrade/front-end-5.2.2604.644+lts
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
    showSearchButton?: boolean;
    searchedQuery?: string;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        searchResults: state.BrasselerDentalCustom.SearchResults,
    };
};

const mapDispatchToProps = {
    getSearchSuggestionsHandler,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    updateSearchResults,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;

    isLoading: boolean;
    clickedProduct: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    searchButton?: React.CSSProperties;
    searchButtonWrapper?: InjectableCss;
    searchBtn?: InjectableCss;
    inputWithBtnWrapper?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 250px;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column-reverse;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
            padding: 0 20px 15px 20px;
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },

    searchBtn: {
        css: css`
            background-color: ${BrasselerColors.blue10}; // dark blue — match your theme token
            color: #fff;
            border: none;
            border-radius: 0 4px 4px 0;
            padding: 0 20px;
            font-weight: 700;
            font-size: 14px;
            cursor: pointer;
            white-space: nowrap;
            height: 85%;
        `,
    },
    inputWithBtnWrapper: {
        css: css`
            display: flex;
            align-items: stretch;
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: props.searchedQuery || "",
            isLoading: false,
            clickedProduct: true,

        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.searchedQuery !== prevProps.searchedQuery) {
            this.setState({ query: this.props.searchedQuery || "" });
            return;
        }

        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            } else {
                // this.closeAutocomplete();
            }
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
        // Only trigger on search page with query param
        const isOnSearchPage = this.props.location.pathname.toLowerCase().startsWith(`/search-page`);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        if (isOnSearchPage && parsedQuery.query) {
            this.setState({ query: parsedQuery.query }, () => {
                this.doSearch(false);
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {};

    doSearch = (shouldCloseSearch = true) => {
        if (this.state.query.trim() === "") {
            return;
        }

        if (shouldCloseSearch) {
            this.props.onBeforeGoToUrl?.();
        }
        this.showAutocomplete = false;
        this.setState({ isLoading: true });
        this.props.getSearchSuggestionsHandler({
            query: this.state.query,
            page: 1,
            pageSize: 9,
            onSuccess: result => {
                this.props.updateSearchResults({
                    searchResult: result,
                });
                this.setState({ isLoading: false });
                
                // ALWAYS go to search-page
                const url = `/search-page?query=${encodedQuery}`;
                this.props.history.push(url);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });

        const encodedQuery = encodeURIComponent(this.state.query);

        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
    };

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = false) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        // this.loadAutocomplete(newQuery);
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement | null>) => { //upgrade/front-end-5.2.2604.644+lts
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    handlePopoverKeyDown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                // this.moveFocus("next");
                break;
            case UP_KEY:
                // this.moveFocus("prev");
                break;
            case ENTER_KEY:
                this.doSearch();

                break;
            default:
                break;
        }
    };
    

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;

        const searchInput = (
            <TextField
                {...styles.input}
                ref={this.props.inputRef}
                isAnimated={this.props.isAnimated}
                iconClickableProps={{ onClick: () => this.doSearch() }}
                value={this.state.query}
                onChange={this.handleInputChange}
                placeholder={translate("Search")}
                autoComplete="off"
                clickableText={translate("submit search")}
                id={searchId}
                data-test-selector="headerSearchInputTextField"
                className="search-input"
            />
        );

        const searchButtonNode = this.props.showSearchButton ? (
            <StyledWrapper as="button" type="button" onClick={() => this.doSearch()} {...styles.searchBtn}>
                {translate("Search")}
            </StyledWrapper>
        ) : null;

        const inputWithButton = this.props.showSearchButton ? (
            <StyledWrapper {...styles.inputWithBtnWrapper}>
                {searchInput}
                {searchButtonNode}
            </StyledWrapper>
        ) : (
            searchInput
        );

        if(this.state.isLoading) {
            return <LoadingSpinner />;
        }


        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <Popover
                    {...styles.popover}
                    controlsId={`${searchId}-popover`}
                    ref={this.popover}
                    insideRefs={[this.list]}
                    popoverTrigger={inputWithButton}
                    positionFunction={this.getPopoverPosition}
                    wrapperProps={styles.popoverWrapper}
                    contentBodyProps={
                        {
                            ref: this.list,
                            ...styles.popoverContentBody,
                        } as any
                    }
                    handleKeyDown={this.handlePopoverKeyDown}
                    onClose={() => {}}
                    className="popover"
                ></Popover>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));